/**
 * Live SSE fetch with a header timeout so a coalesced dead HTTP/2 session cannot
 * hang a new tab forever. After headers arrive the timeout is cleared and the
 * body follows the caller signal (long-lived stream).
 */

export const HUB_STREAM_CONNECT_TIMEOUT_MS = 4_000;

export class HubStreamConnectTimeoutError extends Error {
  constructor(streamName = "Hub live stream") {
    super(`${streamName} did not receive headers in time.`);
    this.name = "HubStreamConnectTimeoutError";
  }
}

export type FetchHubLiveStreamOptions = {
  headers?: HeadersInit;
  signal?: AbortSignal;
  /** Test override. Production uses HUB_STREAM_CONNECT_TIMEOUT_MS. */
  connectTimeoutMs?: number;
};

export async function fetchHubLiveStream(
  url: string,
  options: FetchHubLiveStreamOptions = {},
): Promise<Response> {
  const parentSignal = options.signal;
  const connectTimeoutMs = options.connectTimeoutMs ?? HUB_STREAM_CONNECT_TIMEOUT_MS;
  const connectController = new AbortController();
  let timedOut = false;

  const timeoutId = globalThis.setTimeout(() => {
    timedOut = true;
    connectController.abort();
  }, connectTimeoutMs);

  const onParentAbort = (): void => {
    connectController.abort();
  };
  if (parentSignal != null) {
    if (parentSignal.aborted) {
      connectController.abort();
    } else {
      parentSignal.addEventListener("abort", onParentAbort, { once: true });
    }
  }

  try {
    const response = await fetch(url, {
      headers: options.headers,
      cache: "no-store",
      signal: connectController.signal,
    });
    return response;
  } catch (error) {
    if (timedOut && parentSignal?.aborted !== true) {
      throw new HubStreamConnectTimeoutError();
    }
    throw error;
  } finally {
    globalThis.clearTimeout(timeoutId);
    parentSignal?.removeEventListener("abort", onParentAbort);
  }
}
